import {
  CompletedWorkout,
  Intensity,
  Workout,
  WorkoutDuration,
  WorkoutEquipmentGroup,
  WorkoutStyle,
  WorkoutType,
  WorkoutsMeta,
} from '../dao/core-dao'
import { CompletedWorkoutFilter, WorkoutFilter, coreService } from '../service/core-service'
import { queryKeys } from '../util/query-keys'
import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query'

interface UseCoreChargeWorkoutsOptions extends Omit<UseQueryOptions<Workout[], Error>, 'queryKey' | 'queryFn'> {
  page?: number
  pageSize?: number
}

export const useWorkouts = (
  filter?: WorkoutFilter,
  options?: Omit<UseQueryOptions<Workout[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<Workout[], Error> => {
  return useQuery<Workout[], Error>(queryKeys.workouts(filter), () => coreService.getWorkouts(filter), {
    keepPreviousData: true,
    ...options,
  })
}
type WorkoutsWithMeta = { data: Workout[]; meta?: WorkoutsMeta }
export const useWorkoutsWithMeta = (
  filter?: WorkoutFilter,
  options?: Omit<UseQueryOptions<WorkoutsWithMeta, Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<WorkoutsWithMeta, Error> => {
  return useQuery<WorkoutsWithMeta, Error>(queryKeys.workoutsWithMeta(filter), () => coreService.getWorkoutsWithMeta(filter), {
    keepPreviousData: true,
    ...options,
  })
}
export const usePopularWorkouts = (): UseQueryResult<Workout[], Error> => {
  return useQuery<Workout[], Error>(queryKeys.popularWorkouts(), () => coreService.getWorkouts({ preset: 'popular' }))
}
export const useNewWorkouts = (): UseQueryResult<Workout[], Error> => {
  return useQuery<Workout[], Error>(queryKeys.newWorkouts(), () => coreService.getWorkouts({ preset: 'new-workouts' }))
}
export const useTrendingExpressWorkouts = (): UseQueryResult<Workout[], Error> => {
  return useQuery<Workout[], Error>(queryKeys.trendingExpressWorkouts(), () =>
    coreService.getWorkouts({ preset: 'trending_express' }),
  )
}
export const useCompletedWorkouts = (
  filter?: CompletedWorkoutFilter,
  options?: Omit<UseQueryOptions<CompletedWorkout[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<CompletedWorkout[], Error> => {
  return useQuery<CompletedWorkout[], Error>(
    queryKeys.completedWorkouts(filter),
    () => coreService.getCompletedWorkouts({ filter }),
    {
      keepPreviousData: true,
      ...options,
    },
  )
}
export const useWorkout = (workoutId: string): UseQueryResult<Workout, Error> => {
  return useQuery<Workout, Error>(queryKeys.workout(workoutId), () => coreService.getWorkoutById({ workoutId }), {
    enabled: !!workoutId,
  })
}
export const useWorkoutEquipmentGroups = (): UseQueryResult<WorkoutEquipmentGroup[], Error> => {
  return useQuery<WorkoutEquipmentGroup[], Error>(queryKeys.workoutEquipmentGroups(), () =>
    coreService.listWorkoutEquipmentGroups(),
  )
}
export const useWorkoutDurations = (): UseQueryResult<WorkoutDuration[], Error> => {
  return useQuery<WorkoutDuration[], Error>(queryKeys.workoutDurations(), () => coreService.listWorkoutDurations())
}
export const useWorkoutStyles = (): UseQueryResult<WorkoutStyle[], Error> => {
  return useQuery<WorkoutStyle[], Error>(queryKeys.workoutStyles(), () => coreService.listWorkoutStyles())
}
export const useWorkoutIntensities = (): UseQueryResult<Intensity[], Error> => {
  return useQuery<Intensity[], Error>(queryKeys.workoutIntensities(), () => coreService.listWorkoutIntensities())
}
export const useTodayWorkouts = (): UseQueryResult<Workout[], Error> => {
  return useQuery<Workout[], Error>(queryKeys.todayWorkouts(), () => coreService.getWorkouts({ preset: 'today' }))
}
export const useCompletedWorkoutsInRange = (startDate: number, endDate: number): UseQueryResult<CompletedWorkout[], Error> => {
  return useQuery<CompletedWorkout[], Error>(queryKeys.completedWorkoutsInRange(startDate, endDate), () =>
    coreService.getCompletedWorkouts({
      filter: {
        completedAtAfter: startDate,
        completedAtBefore: endDate,
      },
    }),
  )
}
export const useWorkoutTypes = (): UseQueryResult<WorkoutType[], Error> => {
  return useQuery<WorkoutType[], Error>(queryKeys.workoutTypes(), () => coreService.getWorkoutTypes())
}

export const useCoreChargeWorkouts = (options?: UseCoreChargeWorkoutsOptions): UseQueryResult<Workout[], Error> => {
  const { page, pageSize, ...queryOptions } = options || {}
  return useQuery<Workout[], Error>(
    [...queryKeys.coreChargeWorkouts(), page, pageSize],
    async (): Promise<Workout[]> => coreService.getCoreChargeWorkouts({ page, pageSize }),
    {
      ...queryOptions,
    },
  )
}

interface UseFlexFlowWorkoutsOptions extends Omit<UseQueryOptions<Workout[], Error>, 'queryKey' | 'queryFn'> {
  page?: number
  pageSize?: number
}

export const useFlexFlowWorkouts = (options?: UseFlexFlowWorkoutsOptions): UseQueryResult<Workout[], Error> => {
  const { page, pageSize, ...queryOptions } = options || {}
  return useQuery<Workout[], Error>(
    [...queryKeys.flexFlowWorkouts(), page, pageSize],
    async (): Promise<Workout[]> => coreService.getFlexFlowWorkouts({ page, pageSize }),
    {
      ...queryOptions,
    },
  )
}
